import { Flex, Image, ConnectionItem, Button } from "@wayflyer/flyui";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { connectByPath } from "../../data-sources";
import type { ConnectionPropertiesSchema, ConnectorPropertiesSchema } from "../../data-sources/schemas";

import { useConnectionFlowsConfig } from "../config";

import { COMMON_MESSAGES } from "../common-messages";
import { getAutoInitiateAction } from "./get-auto-initiate-action";

const connectionBadge = (
  connection: ConnectionPropertiesSchema,
  intl: IntlShape,
  supportsReconnection: boolean
): { badgeStatus: "positive" | "critical"; badgeText: string } =>
  connection.isConnected || !supportsReconnection
    ? { badgeStatus: "positive", badgeText: intl.formatMessage(COMMON_MESSAGES.connected) }
    : { badgeStatus: "critical", badgeText: intl.formatMessage(COMMON_MESSAGES.notConnected) };

export const ConnectionsList = ({
  connectorProperties,
  connectForm,
  renderReconnectForm,
}: {
  connectorProperties: ConnectorPropertiesSchema;
  connectForm: ReactNode;
  renderReconnectForm?: (connection: ConnectionPropertiesSchema) => ReactNode;
}) => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const [isFormVisible, setFormVisible] = useState(false);
  const hasAutoInitiated = useRef(false);
  const { supportsReconnection } = useConnectionFlowsConfig();

  const connections = useMemo(() => connectorProperties?.connections ?? [], [connectorProperties]);
  const hasOauthError = searchParams.has("oauth-error");
  const supportsFormReconnect = supportsReconnection && !!renderReconnectForm;

  const [expandedConnectionIds, setExpandedConnectionIds] = useState<ReadonlySet<number>>(() => {
    if (!supportsReconnection) {
      return new Set();
    }
    const initialAction = getAutoInitiateAction(connections, hasOauthError, supportsFormReconnect);
    return initialAction?.type === "expand-form" ? new Set([initialAction.connectionId]) : new Set();
  });

  const toggleReconnectForm = (connectionId: number) =>
    setExpandedConnectionIds((expanded) => {
      const next = new Set(expanded);
      if (next.has(connectionId)) {
        next.delete(connectionId);
      } else {
        next.add(connectionId);
      }
      return next;
    });

  useEffect(() => {
    if (hasAutoInitiated.current || !supportsReconnection) {
      return;
    }
    const action = getAutoInitiateAction(connections, hasOauthError, supportsFormReconnect);
    if (action) {
      hasAutoInitiated.current = true;
      if (action.type === "redirect") {
        connectByPath(action.path);
      }
    }
  }, [connections, hasOauthError, supportsFormReconnect, supportsReconnection]);

  return (
    <Flex direction="column" width="100%" gap="4">
      {connections.length > 0 ? (
        <>
          <Flex direction="column" gap="2">
            {connections.map((connection) => {
              const reconnectsByForm =
                supportsReconnection &&
                !connection.isConnected &&
                supportsFormReconnect &&
                !!connection.reconnectPostPath;
              const reconnectsByRedirect =
                supportsReconnection && !connection.isConnected && !reconnectsByForm && !!connection.reconnectPath;
              return (
                <Flex key={connection.id} direction="column" gap="2">
                  <ConnectionItem
                    label={connection.identifierName ?? connectorProperties.name}
                    logo={
                      connectorProperties.connectionLogoSrc ? (
                        <Image src={connectorProperties.connectionLogoSrc} alt="" />
                      ) : undefined
                    }
                    {...connectionBadge(connection, intl, supportsReconnection)}
                    {...((reconnectsByForm || reconnectsByRedirect) && {
                      actions: [
                        {
                          content: intl.formatMessage(COMMON_MESSAGES.reconnectAction),
                          variant: "Tertiary",
                          onAction: () => {
                            if (reconnectsByForm) {
                              toggleReconnectForm(connection.id);
                            } else {
                              connectByPath(connection.reconnectPath as string);
                            }
                          },
                        },
                      ],
                    })}
                  />
                  {reconnectsByForm && expandedConnectionIds.has(connection.id) && (
                    <Flex direction="column" padding={["4", "0"]} gap="2">
                      {renderReconnectForm?.(connection)}
                    </Flex>
                  )}
                </Flex>
              );
            })}
          </Flex>

          {!isFormVisible && connectorProperties.multipleConnectionsAllowed && (
            <Flex>
              <Button variant="Secondary" size="small" onClick={() => setFormVisible(true)}>
                {intl.formatMessage(COMMON_MESSAGES.addAnother)}
              </Button>
            </Flex>
          )}

          {isFormVisible && (
            <Flex direction="column" padding={["4", "0"]} gap="2">
              {connectForm}
            </Flex>
          )}
        </>
      ) : (
        connectForm
      )}
    </Flex>
  );
};
